import { gettext } from '@eventbrite/i18n';
import React, { ReactNode } from 'react';

export interface EventDescriptionProps extends Performers, Summary {
    shouldShowSalesEndedMessage: boolean | null;
    organizerSalesEndedMessage: string | null;
    salesEndedMessage: string | null;
    hasStructuredContent: boolean;
    structuredContent: StructuredContent;
}

type Performer = {
    name: string;
    biography: string | null;
    image_url: string | null;
};

type Performers = {
    performers: Array<Performer>;
};

type StructuredContent = {
    modules: StructuredContentModule[];
    admat: Admat | null;
};

type Summary = {
    summary: string;
};

interface StructuredContentDescription extends StructuredContent, Summary {}

export enum StructuredContentModuleType {
    TEXT = 'text',
    IMAGE = 'image',
    VIDEO = 'video',
}

type StructuredContentModule = {
    type: StructuredContentModuleType;
} & (TextContentProps | ImageContentProps | VideoContentProps);

export enum TextContentAlignment {
    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
}

type TextContentProps = {
    text: string;
    alignment: TextContentAlignment;
};

type ImageContentProps = {
    url: string;
    altText: string;
    captionText?: string;
};

type VideoContentProps = {
    url: string;
    altText: string;
};

type Admat = {
    imageUrl: string;
    altText: string;
};

const getTextAlignment = (alignment: TextContentAlignment) => {
    switch (alignment) {
        case TextContentAlignment.LEFT:
            return 'eds-text--left';
        case TextContentAlignment.CENTER:
            return 'eds-text--center';
        case TextContentAlignment.RIGHT:
            return 'eds-text--right';
    }
};

const ContentWrapper = ({
    style,
    children,
}: {
    style?: string;
    children?: ReactNode;
}) => {
    const className = `eds-l-mar-vert-6 eds-l-sm-mar-vert-4 eds-text-bm ${style}`;

    return <div className={className}>{children}</div>;
};

const TextContent = ({ text, alignment }: TextContentProps) => (
    <ContentWrapper style="structured-content-rich-text">
        <div
            className={getTextAlignment(alignment)}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: text }}
        />
    </ContentWrapper>
);

const VideoContent = ({ url, altText }: VideoContentProps) => (
    <ContentWrapper style="structured-content-video">
        <iframe
            className="structured-content-video__iframe"
            src={url}
            frameBorder="0"
            allowFullScreen
            title={altText}
        >
            {altText}
        </iframe>
    </ContentWrapper>
);

const ImageContent = ({ url, altText, captionText }: ImageContentProps) => (
    <ContentWrapper>
        <img className="eds-max-img" src={url} alt={altText} />
        {captionText && (
            <span className="eds-text-bs eds-text-color--ui-600">
                {captionText}
            </span>
        )}
    </ContentWrapper>
);

const Admat = ({ imageUrl, altText }: Admat) => (
    <ImageContent url={imageUrl} altText={altText} />
);

const StructuredModuleRenderer = ({
    module,
}: {
    module: StructuredContentModule;
}) => {
    switch (module.type) {
        case StructuredContentModuleType.TEXT:
            const textContent = module as TextContentProps;
            return (
                <TextContent
                    text={textContent.text}
                    alignment={textContent.alignment}
                />
            );
        case StructuredContentModuleType.IMAGE:
            const imageContent = module as ImageContentProps;
            return (
                <ImageContent
                    url={imageContent.url}
                    altText={imageContent.altText}
                    captionText={imageContent.captionText}
                />
            );
        case StructuredContentModuleType.VIDEO:
            const videoContent = module as VideoContentProps;
            return (
                <VideoContent
                    url={videoContent.url}
                    altText={videoContent.altText}
                />
            );
        default:
            return <></>;
    }
};

const OrganizerSalesEndedMessage = ({
    organizerSalesEndedMessage,
    salesEndedMessage,
}: Pick<
    EventDescriptionProps,
    'organizerSalesEndedMessage' | 'salesEndedMessage'
>) => (
    <div className="eds-l-lg-mar-bot-6 eds-l-md-mar-bot-4 eds-l-pad-bot-6 eds-hide-small">
        <h2 className="eds-is-hidden-accessible">
            {gettext('Sales Have Ended')}
        </h2>
        <div className="eds-text-bl">{organizerSalesEndedMessage}</div>
        <div className="eds-l-mar-top-1">
            <p className="eds-text-bm">{salesEndedMessage}</p>
        </div>
    </div>
);

const StructuredContentDescription = ({
    summary,
    admat,
    modules,
}: StructuredContentDescription) => {
    return (
        <>
            <div className="eds-l-mar-bot-8">
                <p className="eds-text-bm">
                    {/* Summary comes from the summary field in the creation flow */}
                    <strong
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: summary,
                        }}
                    ></strong>
                </p>
            </div>
            <div className="eds-l-mar-bot-8 structured-content">
                {admat && (
                    <Admat imageUrl={admat.imageUrl} altText={admat.altText} />
                )}
                <h2 className="eds-text-bl eds-l-mar-bot-4">
                    {gettext('About this event')}
                </h2>
                <div className="has-user-generated-content">
                    {modules.map((module, index) => (
                        <StructuredModuleRenderer module={module} key={index} />
                    ))}
                </div>
            </div>
        </>
    );
};

const OldDescriptionBeforeStructuredContent = ({ summary }: Summary) => {
    return (
        <div className="eds-l-mar-bot-8 eds-l-pad-bot-6">
            <h3 className="eds-text-bl">{gettext('Description')}</h3>
            <div className="eds-text-bm">
                {/* Summary comes from an old creation flow */}
                <div
                    className="l-mar-top-3"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: summary }}
                ></div>
            </div>
        </div>
    );
};

const Performers = ({ performers }: Performers) => {
    return (
        <div className="eds-l-mar-bot-6 eds-l-sm-mar-bot-4 has-user-generated-content">
            <ul>
                {performers.map((performer, index) => (
                    <li key={index}>
                        <div className="eds-l-mar-top-6 eds-l-mar-bot-1 eds-l-sm-mar-vert-4 eds-text-bl ">
                            {performer.name}
                        </div>
                        {performer.biography && (
                            <div
                                className="structured-content-rich-text eds-l-mar-vert-1 eds-l-sm-mar-vert-4 eds-text-bm"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: performer.biography,
                                }}
                            />
                        )}
                        {performer.image_url && (
                            <div className="eds-l-mar-vert-1 eds-l-sm-mar-vert-4 eds-text-bm">
                                <img
                                    className="eds-max-img"
                                    src={performer.image_url}
                                    alt={performer.name}
                                />
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export const EventDescription = ({
    shouldShowSalesEndedMessage,
    organizerSalesEndedMessage,
    salesEndedMessage,
    hasStructuredContent,
    summary,
    performers,
    structuredContent: { modules = [], admat },
}: EventDescriptionProps) => (
    <>
        {shouldShowSalesEndedMessage && (
            <OrganizerSalesEndedMessage
                organizerSalesEndedMessage={organizerSalesEndedMessage}
                salesEndedMessage={salesEndedMessage}
            />
        )}

        {hasStructuredContent ? (
            <StructuredContentDescription
                admat={admat}
                modules={modules}
                summary={summary}
            />
        ) : (
            <OldDescriptionBeforeStructuredContent summary={summary} />
        )}

        {performers.length > 0 && <Performers performers={performers} />}
    </>
);
